/**
 * HotelbedsExploreSearch — Phase 5 (2026-07-27).
 *
 * Hero-position search bar for `/explore`. Replaces the deprecated
 * Google-Places-backed `SmartSearchBar` + trending row + filters
 * combo. Single-purpose: pick a Hotelbeds destination, tweak the
 * date range + guest count, submit → land on `/hotels/search?...`
 * (same param shape the dedicated `/hotels` page already emits, so
 * `HotelsResultsPage` is happy).
 *
 * Design intent:
 *   • The whole thing sits on top of the hero image; navy glass
 *     surface for legibility.
 *   • Destination autocomplete fires only on user input via a 250ms
 *     debounce → GET /api/hotels/destinations/search.
 *   • Dates default to today+30 → today+34 (4-night stay) which lands
 *     inside HB's typical free-cancellation window for most
 *     inventory. Guests default to 2 adults, 0 children — the modal
 *     stepper covers the rest.
 *   • Nothing is submitted until the user clicks the search button —
 *     no autoload, no live-fire keystroke API storm.
 */
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MapPin, Search, Calendar, Users, Minus, Plus } from "lucide-react";
import { api } from "@/lib/api";

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function useDebounced(v, ms = 250) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

function Stepper({ label, value, onChange, min = 0, max = 8, testId }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="font-hand text-white/90 text-sm">{label}</span>
      <div className="inline-flex items-center gap-2">
        <button
          type="button"
          data-testid={`${testId}-dec`}
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-8 h-8 rounded-full border-2 border-white/40 text-white disabled:opacity-30 flex items-center justify-center tb-tap hover:bg-white/10"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span
          data-testid={`${testId}-value`}
          className="min-w-[1.75rem] text-center font-hand text-white text-base"
        >
          {value}
        </span>
        <button
          type="button"
          data-testid={`${testId}-inc`}
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          className="w-8 h-8 rounded-full border-2 border-white/40 text-white disabled:opacity-30 flex items-center justify-center tb-tap hover:bg-white/10"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function HotelbedsExploreSearch() {
  const nav = useNavigate();
  const location = useLocation();
  const [dest, setDest] = useState(null); // {code, name, country}
  const [destQuery, setDestQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [checkIn, setCheckIn] = useState(todayPlus(30));
  const [checkOut, setCheckOut] = useState(todayPlus(34));
  const [adults, setAdults] = useState(2);
  const [children, setChildrenCount] = useState(0);
  const [guestsOpen, setGuestsOpen] = useState(false);
  const debounced = useDebounced(destQuery, 250);
  const boxRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const q = (debounced || "").trim();
    // Empty query returns the popular starter list (limit 8).
    const url = q
      ? `/hotels/destinations/search?q=${encodeURIComponent(q)}&limit=10`
      : "/hotels/destinations/search?q=&limit=8";
    api.get(url)
      .then(({ data }) => { if (alive) setSuggestions(data || []); })
      .catch(() => { if (alive) setSuggestions([]); });
    return () => { alive = false; };
  }, [debounced]);

  // Close the dropdown when clicking outside.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const canSubmit = Boolean(
    dest && checkIn && checkOut && checkOut > checkIn && adults >= 1,
  );

  const onSubmit = () => {
    if (!canSubmit) return;
    const occ = {
      rooms: 1,
      adults,
      children,
      paxes: [
        ...Array.from({ length: adults }, () => ({ type: "AD", age: 30 })),
        ...Array.from({ length: children }, () => ({ type: "CH", age: 8 })),
      ],
    };
    const params = new URLSearchParams({
      destinationCode: dest.code,
      destinationName: dest.name,
      checkIn,
      checkOut,
      occupancies: JSON.stringify([occ]),
    });
    // Phase 5 issue-fix (2026-07-27): stash the origin path in
    // `history.state` so the results page's back button can bounce
    // back to /explore (or wherever else the user was) rather than
    // hard-coding /hotels. `replace: false` (default) keeps the
    // Explore entry in the browser history stack so the browser's
    // native back button also works.
    nav(`/hotels/search?${params.toString()}`, {
      state: { from: location.pathname + location.search },
    });
  };

  const guestsLabel = children > 0
    ? `${adults} adults · ${children} kids`
    : `${adults} adult${adults === 1 ? "" : "s"}`;

  return (
    <div
      ref={boxRef}
      data-testid="explore-hotelbeds-search"
      className="relative w-full max-w-3xl mx-auto rounded-3xl bg-tb-navy-deep/85 backdrop-blur-md border border-white/25 p-4 md:p-5 shadow-[0_18px_50px_-18px_rgba(0,0,0,0.55)]"
    >
      {/* Destination */}
      <label className="block font-hand text-white/60 text-[10px] uppercase tracking-widest">
        Where to
      </label>
      <div className="mt-1 flex items-center gap-2 border-b border-white/25 focus-within:border-tb-gold py-2">
        <MapPin className="w-4 h-4 text-tb-gold-bright shrink-0" />
        <input
          data-testid="explore-hotelbeds-destination"
          type="text"
          placeholder="Search hotels by city or country"
          value={dest ? `${dest.name}, ${dest.country}` : destQuery}
          onChange={(e) => {
            setDest(null);
            setDestQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canSubmit) onSubmit();
          }}
          className="flex-1 bg-transparent outline-none font-hand text-white text-lg placeholder:text-white/40"
        />
      </div>
      {open && suggestions.length > 0 && (
        <ul
          data-testid="explore-hotelbeds-suggestions"
          className="absolute z-30 left-4 right-4 md:left-5 md:right-5 mt-1 max-h-72 overflow-y-auto bg-white border border-tb-navy/20 rounded-xl shadow-2xl"
        >
          {suggestions.map((s) => (
            <li key={s.code}>
              <button
                type="button"
                data-testid={`explore-hotelbeds-suggestion-${s.code}`}
                onMouseDown={(e) => {
                  // Use mousedown so it fires before the input's blur;
                  // otherwise the click is swallowed by the outside-click
                  // handler above.
                  e.preventDefault();
                  setDest(s);
                  setDestQuery("");
                  setOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 hover:bg-tb-cream-2 flex items-center gap-2 font-hand text-tb-navy tb-tap"
              >
                <MapPin className="w-3.5 h-3.5 text-tb-navy/60" />
                <span className="font-medium">{s.name}</span>
                <span className="text-tb-navy/50 text-sm">{s.country || s.countryCode}</span>
                <span className="ml-auto text-tb-navy/40 text-[11px] uppercase tracking-widest">
                  {s.code}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Dates + guests row */}
      <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr] gap-3">
        <div>
          <label className="block font-hand text-white/60 text-[10px] uppercase tracking-widest">
            Check-in
          </label>
          <div className="flex items-center gap-2 border-b border-white/25 focus-within:border-tb-gold py-2">
            <Calendar className="w-4 h-4 text-tb-gold-bright shrink-0" />
            <input
              data-testid="explore-hotelbeds-checkin"
              type="date"
              value={checkIn}
              min={todayPlus(1)}
              max={todayPlus(720)}
              onChange={(e) => setCheckIn(e.target.value)}
              className="flex-1 bg-transparent outline-none font-hand text-white text-base"
              style={{ colorScheme: "dark" }}
            />
          </div>
        </div>
        <div>
          <label className="block font-hand text-white/60 text-[10px] uppercase tracking-widest">
            Check-out
          </label>
          <div className="flex items-center gap-2 border-b border-white/25 focus-within:border-tb-gold py-2">
            <Calendar className="w-4 h-4 text-tb-gold-bright shrink-0" />
            <input
              data-testid="explore-hotelbeds-checkout"
              type="date"
              value={checkOut}
              min={checkIn || todayPlus(2)}
              max={todayPlus(725)}
              onChange={(e) => setCheckOut(e.target.value)}
              className="flex-1 bg-transparent outline-none font-hand text-white text-base"
              style={{ colorScheme: "dark" }}
            />
          </div>
        </div>
        <div className="relative">
          <label className="block font-hand text-white/60 text-[10px] uppercase tracking-widest">
            Guests
          </label>
          <button
            type="button"
            data-testid="explore-hotelbeds-guests-toggle"
            onClick={() => setGuestsOpen((v) => !v)}
            className="w-full flex items-center gap-2 border-b border-white/25 focus:border-tb-gold py-2 font-hand text-white text-base outline-none"
          >
            <Users className="w-4 h-4 text-tb-gold-bright shrink-0" />
            <span data-testid="explore-hotelbeds-guests-label" className="flex-1 text-left">
              {guestsLabel}
            </span>
          </button>
          {guestsOpen && (
            <div
              data-testid="explore-hotelbeds-guests-panel"
              className="absolute z-30 right-0 mt-2 w-64 rounded-xl bg-tb-navy-deep border border-white/25 p-3 shadow-2xl"
            >
              <Stepper
                testId="explore-hotelbeds-adults"
                label="Adults (18+)"
                value={adults}
                onChange={setAdults}
                min={1}
                max={8}
              />
              <Stepper
                testId="explore-hotelbeds-children"
                label="Children (0–17)"
                value={children}
                onChange={setChildrenCount}
                min={0}
                max={8}
              />
              <button
                type="button"
                data-testid="explore-hotelbeds-guests-done"
                onClick={() => setGuestsOpen(false)}
                className="mt-2 w-full px-3 py-1.5 rounded-full bg-tb-gold-bright text-tb-navy-deep font-hand text-sm hover:brightness-110 tb-tap"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Submit */}
      <button
        type="button"
        data-testid="explore-hotelbeds-submit"
        disabled={!canSubmit}
        onClick={onSubmit}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-tb-gold-bright text-tb-navy-deep font-hand text-base tb-tap disabled:opacity-45 hover:brightness-110 border-2 border-tb-gold-bright"
      >
        <Search className="w-5 h-5" />
        Search hotels
      </button>
    </div>
  );
}
